import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BookMarked, CheckCircle2, ChevronLeft, ChevronRight, ExternalLink, Lock, Maximize2, Minimize2 } from 'lucide-react';

// Lazy-loaded so pdfjs-dist stays out of the main bundle.
const PdfDeck = lazy(() => import('./PdfDeck'));
import { useAuth } from '../contexts/AuthContext';
import ReadingBody, { IMG_SRC_RE } from './ReadingBody';
import YourPath from './YourPath';
import { useCatalogTrack, useMyProgress } from '../lib/data';
import { examLockMessage, examLockState } from '../lib/exam-lock';
import { completeStep, getQuiz, scoreQuiz, type QuizQuestion } from '../lib/api';
import type { CompleteStepResult, Step } from '../types';
import SiteHeader from './SiteHeader';
import RewardMoment from './RewardMoment';
import AssignmentChecklist from './AssignmentChecklist';
import VivaSignoff from './VivaSignoff';

export default function StepView() {
  const { trackId, stepId } = useParams<{ trackId: string; stepId: string }>();
  const { user, isCandidate, isAdmin } = useAuth();
  const navigate = useNavigate();
  // All audiences go through the catalog API — Firestore rules now only allow
  // direct track/step reads for admins, so we always use the access-checked API.
  const { steps, loading } = useCatalogTrack(trackId);
  const { progress, ready: progressReady } = useMyProgress(user?.uid, trackId);

  const step = useMemo(() => steps.find((s) => s.id === stepId), [steps, stepId]);
  const alreadyDone = progress?.steps?.[stepId ?? '']?.done === true;
  const savedReflection = progress?.steps?.[stepId ?? '']?.reflection ?? '';
  // Steps come from the catalog API for members and from the same API on this
  // page for admins (who can also read the step docs directly). Both shapes
  // carry order + masteryMinCorrect, which is all the gate needs. Hold the
  // banner until progress has loaded so a finished learner is not told "0 of N".
  const examLockText = useMemo(() => {
    if (!progressReady || !stepId) return null;
    return examLockMessage(examLockState(steps, progress, stepId));
  }, [progressReady, stepId, steps, progress]);

  const [reflection, setReflection] = useState('');
  const [attested, setAttested] = useState(false);
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reward, setReward] = useState<CompleteStepResult | null>(null);

  const nextStep = useMemo(() => {
    if (!step) return null;
    const idx = steps.findIndex((s) => s.id === step.id);
    return idx >= 0 ? steps[idx + 1] ?? null : null;
  }, [steps, step]);

  // After the LAST step, staff are nudged to the Dangal; candidates (who have no
  // Dangal) go back to their trail instead.
  const fallbackHref = isCandidate ? `/track/${trackId}` : '/dangal';
  const fallbackLabel = isCandidate ? 'Back to kushti' : 'See the Dangal';
  const nextHref = nextStep ? `/track/${trackId}/step/${nextStep.id}` : fallbackHref;
  const nextLabel = nextStep ? 'Next paintra' : fallbackLabel;

  const onComplete = async () => {
    if (busy || !trackId || !stepId) return;
    setBusy(true);
    setError(null);
    const res = await completeStep(trackId, stepId, reflection.trim(), evidenceUrl.trim() || undefined);
    setBusy(false);
    if (res.ok && (res.data as CompleteStepResult)?.ok) {
      setReward(res.data as CompleteStepResult);
    } else {
      const code = (res.data as { error?: string })?.error || `http_${res.status}`;
      setError(prettyError(code));
    }
  };

  return (
    <div className="min-h-screen bg-paper-50 dark:bg-clay-floor">
      <SiteHeader />
      <main className="mx-auto max-w-2xl px-4 pb-20 pt-4">
        <Link
          to={`/track/${trackId}`}
          className="inline-flex items-center gap-1 text-sm text-ink-400 hover:text-terracotta-600"
        >
          <ArrowLeft className="h-4 w-4" /> Back to kushti
        </Link>

        {loading ? (
          <div className="py-12 text-center text-sm text-ink-400">Loading paintra…</div>
        ) : !step ? (
          <div className="py-12 text-center text-sm text-ink-400">Paintra not found.</div>
        ) : (
          <div className="mt-3 animate-rise-in">
            <p className="text-xs font-semibold uppercase tracking-wider text-terracotta-500">
              {step.type} · +{step.points} Zor
            </p>
            <h1 className="mt-1 text-xl font-semibold text-ink-900 dark:text-paper-50">{step.title}</h1>

            <StepBody
              step={step}
              trackId={trackId!}
              attested={attested}
              setAttested={setAttested}
              evidenceUrl={evidenceUrl}
              uid={user?.uid}
              setEvidenceUrl={setEvidenceUrl}
            />

            <FurtherReading references={step.references} />

            {step.type === 'quiz' ? (
              <QuizBody
                trackId={trackId!}
                stepId={stepId!}
                alreadyDone={alreadyDone}
                savedReflection={savedReflection}
                nextHref={nextHref}
                nextLabel={nextLabel}
                setReward={setReward}
                masteryMinCorrect={step.masteryMinCorrect}
                examLockText={examLockText}
              />
            ) : step.completion === 'khalifa' && isAdmin ? (
              <>
                {alreadyDone && <DonePanel savedReflection={savedReflection} nextHref={nextHref} nextLabel={nextLabel} />}
                <VivaSignoff trackId={trackId!} stepId={stepId!} isAdmin />
              </>
            ) : alreadyDone ? (
              <DonePanel
                savedReflection={savedReflection}
                nextHref={nextHref}
                nextLabel={nextLabel}
              />
            ) : step.completion === 'checker' ? (
              <AssignmentChecklist trackId={trackId!} stepId={stepId!} onCompleted={setReward} />
            ) : step.completion === 'khalifa' ? (
              <VivaSignoff trackId={trackId!} stepId={stepId!} isAdmin={false} />
            ) : (
              <div className="mt-5">
                <label
                  htmlFor="reflection"
                  className="block text-sm font-medium text-ink-700 dark:text-paper-200"
                >
                  One line — what did you learn?
                </label>
                <input
                  id="reflection"
                  type="text"
                  maxLength={280}
                  value={reflection}
                  onChange={(e) => setReflection(e.target.value)}
                  placeholder="The one thing that stuck…"
                  className="mt-1.5 w-full rounded-xl border border-rule bg-paper-0 px-3.5 py-2.5 text-[15px] text-ink-900 outline-none ring-terracotta-200 placeholder:text-ink-300 focus:border-terracotta-300 focus:ring-2 dark:border-clay-rule dark:bg-clay-surface dark:text-paper-50 dark:placeholder:text-paper-300"
                />

                {error && <p className="mt-3 text-sm text-terracotta-700">{error}</p>}

                <button
                  onClick={() => void onComplete()}
                  disabled={busy || (needsAttest(step) && !attested)}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-terracotta-500 px-5 py-3 text-[15px] font-semibold text-white shadow-sm transition hover:bg-terracotta-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <CheckCircle2 className="h-5 w-5" />
                  {busy ? 'Saving…' : `Mark done · +${step.points} Zor`}
                </button>
                {needsAttest(step) && !attested && (
                  <p className="mt-2 text-center text-xs text-ink-400">
                    Confirm you completed it above to earn your Zor.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {reward && step && (
        <RewardMoment
          result={reward}
          stepTitle={step.title}
          onClose={() => {
            setReward(null);
            if (nextStep) navigate(`/track/${trackId}/step/${nextStep.id}`);
            else navigate(`/track/${trackId}`);
          }}
          // Candidates have no Dangal — omit the CTA entirely for them.
          onSeeDangal={isCandidate ? undefined : () => navigate('/dangal')}
        />
      )}
    </div>
  );
}

function needsAttest(step: Step): boolean {
  return step.type === 'external' && !step.completion;
}

function StepBody({
  step,
  trackId,
  attested,
  setAttested,
  evidenceUrl,
  setEvidenceUrl,
  uid,
}: {
  step: Step;
  trackId: string;
  attested: boolean;
  setAttested: (v: boolean) => void;
  evidenceUrl: string;
  setEvidenceUrl: (v: string) => void;
  uid?: string;
}) {
  if (step.type === 'reading') {
    return (
      <ReadingBody content={step.content} path={uid ? <YourPath uid={uid} candidate={true} /> : undefined} />
    );
  }

  if (step.type === 'slides') {
    return <SlidesBody step={step} trackId={trackId} />;
  }

  if (
    step.type === 'video' &&
    step.bunnyVideoId &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(step.bunnyVideoId)
  ) {
    const src = `https://iframe.mediadelivery.net/embed/762674/${step.bunnyVideoId}?autoplay=false&preload=true&responsive=true`;
    return (
      <div className="mt-4 overflow-hidden rounded-xl border border-rule bg-black dark:border-clay-rule">
        <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
          <iframe
            title={step.title}
            src={src}
            className="absolute inset-0 h-full w-full"
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            loading="lazy"
          />
        </div>
      </div>
    );
  }

  if (step.type === 'video' && step.mediaSrc && /^\/content\/studio\/[a-z0-9-]+\.mp4$/.test(step.mediaSrc)) {
    const captions = step.mediaSrc.replace(/\.mp4$/, '.vtt');
    return (
      <div className="mt-4 overflow-hidden rounded-xl border border-rule bg-black dark:border-clay-rule">
        <video
          className="aspect-video w-full"
          controls
          playsInline
          preload="metadata"
          src={step.mediaSrc}
        >
          <track kind="captions" srcLang="en" label="English" src={captions} default />
        </video>
      </div>
    );
  }

  if (step.type === 'video' && step.vimeoId) {
    // Real Vimeo ids are numeric; a placeholder (e.g. 'REPLACE_ME') gets a tidy
    // "coming soon" card instead of a broken embed. The step is still completable.
    if (!/^\d{6,}$/.test(step.vimeoId)) {
      return (
        <div className="mt-4 rounded-xl border border-dashed border-rule p-6 text-center text-sm text-ink-400 dark:border-clay-rule">
          🎬 Video coming soon — a Henoyo clip will appear here. You can still mark this paintra done.
        </div>
      );
    }
    return (
      <div className="mt-4 overflow-hidden rounded-xl border border-rule bg-black dark:border-clay-rule">
        <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
          <iframe
            title={step.title}
            src={`https://player.vimeo.com/video/${encodeURIComponent(step.vimeoId)}?dnt=1&title=0&byline=0&portrait=0${
              step.vimeoHash && /^[A-Za-z0-9]{6,20}$/.test(step.vimeoHash)
                ? `&h=${encodeURIComponent(step.vimeoHash)}`
                : ''
            }`}
            className="absolute inset-0 h-full w-full"
            loading="lazy"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    );
  }

  if (step.type === 'video' && step.youtubeId) {
    const YT_ID_RE = /^[A-Za-z0-9_-]{11}$/;
    const id = step.youtubeId;
    if (!YT_ID_RE.test(id)) {
      return (
        <div className="mt-4 rounded-xl border border-rule bg-paper-0 p-4 text-sm text-ink-500 dark:border-clay-rule dark:bg-clay-surface">
          Video unavailable.
        </div>
      );
    }
    return (
      <div className="mt-4 overflow-hidden rounded-xl border border-rule bg-black dark:border-clay-rule">
        <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
          <iframe
            title={step.title}
            src={`https://www.youtube-nocookie.com/embed/${id}`}
            className="absolute inset-0 h-full w-full"
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="accelerometer; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
          />
        </div>
      </div>
    );
  }

  if (step.type === 'external') {
    return (
      <div className="mt-4 rounded-xl border border-rule bg-paper-0 p-4 dark:border-clay-rule dark:bg-clay-surface">
        {step.preview?.title && (
          <p className="font-medium text-ink-900 dark:text-paper-50">{step.preview.title}</p>
        )}
        {step.preview?.description && (
          <p className="mt-1 text-sm text-ink-500 dark:text-paper-300">{step.preview.description}</p>
        )}
        {step.url && /^https?:\/\//i.test(step.url) && (
          <a
            href={step.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-rule px-3 py-1.5 text-sm font-medium text-terracotta-600 hover:bg-terracotta-50 dark:border-clay-rule dark:text-terracotta-300 dark:hover:bg-terracotta-900/40"
          >
            Open <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
        {!step.completion && (
        <label className="mt-4 flex items-start gap-2 text-sm text-ink-700 dark:text-paper-200">
          <input
            type="checkbox"
            checked={attested}
            onChange={(e) => setAttested(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-terracotta-500 dark:accent-terracotta-400"
          />
          I completed this and the details are accurate.
        </label>
        )}
        {step.evidence === 'url' && !step.completion && (
          <input
            type="url"
            value={evidenceUrl}
            onChange={(e) => setEvidenceUrl(e.target.value)}
            placeholder="Paste your badge / credential URL (optional)"
            className="mt-3 w-full rounded-lg border border-rule bg-paper-50 px-3 py-2 text-sm outline-none focus:border-terracotta-300 dark:border-clay-rule dark:bg-clay-floor"
          />
        )}
      </div>
    );
  }

  // Quiz body is rendered at the StepView level (needs top-level state).
  return null;
}

function DonePanel({
  savedReflection,
  nextHref,
  nextLabel,
}: {
  savedReflection: string;
  nextHref: string;
  nextLabel: string;
}) {
  return (
    <div className="mt-5 rounded-xl border border-verdant-200 bg-verdant-200/30 p-4 dark:border-verdant-700 dark:bg-verdant-700/15">
      <p className="flex items-center gap-2 font-medium text-verdant-700 dark:text-verdant-200">
        <CheckCircle2 className="h-5 w-5" /> Done — Zor in the bank.
      </p>
      {savedReflection && (
        <p className="mt-2 text-sm italic text-ink-600 dark:text-paper-300">“{savedReflection}”</p>
      )}
      <Link
        to={nextHref}
        className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-terracotta-600 hover:text-terracotta-700"
      >
        {nextLabel} →
      </Link>
    </div>
  );
}

type QuizLoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; questions: QuizQuestion[] };

function ExamLockBanner({ trackId, message }: { trackId: string; message: string }) {
  return (
    <aside
      role="status"
      className="rounded-xl border-l-4 border-ink-400 bg-paper-100 px-4 py-3 text-sm text-ink-700 dark:border-paper-300 dark:bg-clay-raised dark:text-paper-200"
    >
      <p className="flex items-start gap-2">
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-ink-500 dark:text-paper-300" aria-hidden />
        <span>
          {message}{' '}
          <Link
            to={`/track/${trackId}`}
            className="font-semibold text-terracotta-600 hover:text-terracotta-700 dark:text-terracotta-300"
          >
            Back to the track
          </Link>
        </span>
      </p>
    </aside>
  );
}

function QuizBody({
  trackId,
  stepId,
  alreadyDone,
  savedReflection,
  nextHref,
  nextLabel,
  setReward,
  masteryMinCorrect,
  examLockText,
}: {
  trackId: string;
  stepId: string;
  alreadyDone: boolean;
  savedReflection: string;
  nextHref: string;
  nextLabel: string;
  setReward: (r: CompleteStepResult) => void;
  masteryMinCorrect?: number;
  examLockText: string | null;
}) {
  const [quizState, setQuizState] = useState<QuizLoadState>({ phase: 'loading' });
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [reflection, setReflection] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shortfall, setShortfall] = useState<string | null>(null);
  const [bar, setBar] = useState<number | undefined>(masteryMinCorrect);

  useEffect(() => {
    if (alreadyDone) return;
    let cancelled = false;
    setQuizState({ phase: 'loading' });
    void (async () => {
      const res = await getQuiz(trackId, stepId);
      if (cancelled) return;
      if (!res.ok) {
        setQuizState({
          phase: 'error',
          message: `Failed to load quiz (HTTP ${res.status}).`,
        });
        return;
      }
      if (typeof res.data?.masteryMinCorrect === 'number') setBar(res.data.masteryMinCorrect);
      setQuizState({
        phase: 'ready',
        questions: Array.isArray(res.data?.questions) ? res.data.questions : [],
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [trackId, stepId, alreadyDone]);

  if (alreadyDone) {
    return (
      <DonePanel
        savedReflection={savedReflection}
        nextHref={nextHref}
        nextLabel={nextLabel}
      />
    );
  }

  const banner = examLockText ? <ExamLockBanner trackId={trackId} message={examLockText} /> : null;

  const onSubmit = async () => {
    if (busy || examLockText) return;
    setBusy(true);
    setError(null);
    setShortfall(null);
    const res = await scoreQuiz(trackId, stepId, answers, reflection.trim() || undefined);
    setBusy(false);
    if (res.ok && (res.data as CompleteStepResult)?.ok) {
      const data = res.data as CompleteStepResult;
      if (data.passed === false) {
        const need = data.masteryMinCorrect ?? bar;
        setShortfall(
          `You got ${data.correct} of ${data.total}. This exam needs ${need}. It stays open — change any answer and submit again.`,
        );
      } else {
        setReward(data);
      }
    } else {
      const code = (res.data as { error?: string })?.error ?? `http_${res.status}`;
      setError(prettyError(code));
    }
  };

  if (quizState.phase === 'loading') {
    return (
      <div className="mt-4 flex flex-col gap-4">
        {banner}
        <p className="text-sm text-ink-400">Loading quiz…</p>
      </div>
    );
  }

  if (quizState.phase === 'error') {
    return (
      <div className="mt-4 flex flex-col gap-4">
        {banner}
        <p className="text-sm text-terracotta-700">{quizState.message}</p>
      </div>
    );
  }

  const { questions } = quizState;
  const allAnswered =
    questions.length > 0 && questions.every((q) => answers[q.id] !== undefined);

  return (
    <div className="mt-4 flex flex-col gap-4">
      {banner}
      {bar !== undefined && (
        <p className="text-sm text-ink-600 dark:text-paper-200">
          This exam needs {bar} of {questions.length}. Zor and the badge count once you reach it.
        </p>
      )}
      {questions.map((q, qi) => (
        <fieldset
          key={q.id}
          className="rounded-xl border border-rule bg-paper-0 p-4 dark:border-clay-rule dark:bg-clay-surface"
        >
          {/* float keeps the legend inside the box; a plain legend is drawn on top of the border */}
          <legend className="float-left w-full text-sm font-medium text-ink-900 dark:text-paper-50">
            {qi + 1}. {q.prompt}
          </legend>
          <div className="clear-both flex flex-col gap-2 pt-3">
            {q.options.map((opt, oi) => (
              <label
                key={oi}
                className="flex items-start gap-2 text-sm text-ink-700 dark:text-paper-200"
              >
                <input
                  type="radio"
                  name={`q-${q.id}`}
                  value={oi}
                  checked={answers[q.id] === oi}
                  onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: oi }))}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-terracotta-500 dark:accent-terracotta-400"
                />
                {opt}
              </label>
            ))}
          </div>
        </fieldset>
      ))}

      <div>
        <label
          htmlFor="quiz-reflection"
          className="block text-sm font-medium text-ink-700 dark:text-paper-200"
        >
          One line — what did you learn?{' '}
          <span className="font-normal text-ink-400">(optional)</span>
        </label>
        <input
          id="quiz-reflection"
          type="text"
          maxLength={280}
          value={reflection}
          onChange={(e) => setReflection(e.target.value)}
          placeholder="The one thing that stuck…"
          className="mt-1.5 w-full rounded-xl border border-rule bg-paper-0 px-3.5 py-2.5 text-[15px] text-ink-900 outline-none ring-terracotta-200 placeholder:text-ink-300 focus:border-terracotta-300 focus:ring-2 dark:border-clay-rule dark:bg-clay-surface dark:text-paper-50 dark:placeholder:text-paper-300"
        />
      </div>

      {shortfall && <p className="text-sm text-ink-700 dark:text-paper-200">{shortfall}</p>}
      {error && <p className="text-sm text-terracotta-700">{error}</p>}

      <button
        onClick={() => void onSubmit()}
        disabled={busy || !allAnswered || examLockText !== null}
        title={examLockText ?? undefined}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-terracotta-500 px-5 py-3 text-[15px] font-semibold text-white shadow-sm transition hover:bg-terracotta-600 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <CheckCircle2 className="h-5 w-5" />
        {busy ? 'Saving…' : 'Submit answers'}
      </button>
    </div>
  );
}

// Reading lesson body. Renders structured blocks as React children (NEVER raw
// HTML). Staff read steps straight from Firestore (bypassing the catalog API's
// server-side scrub), so we coerce to known block shapes here too — defense in
// depth: unknown kinds dropped, text forced to strings, image src whitelisted.

// Is this an uploaded PDF in OUR Firebase Storage bucket? Those load through the
// access-checked SAME-ORIGIN proxy (no cross-origin CORS); externally-pasted PDFs
// stay direct. Strict URL parse (host + bucket family), not a substring match.
function isOurStoragePdf(url: string): boolean {
  const bucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string | undefined;
  if (!bucket) return false;
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:' || u.hostname !== 'firebasestorage.googleapis.com') return false;
    const m = /^\/v0\/b\/([^/]+)\/o\//.exec(u.pathname);
    if (!m) return false;
    const b = decodeURIComponent(m[1]);
    const base = bucket.replace(/\.(firebasestorage\.app|appspot\.com)$/, '');
    return b === bucket || b === `${base}.firebasestorage.app` || b === `${base}.appspot.com`;
  } catch {
    return false;
  }
}

function SlidesBody({ step, trackId }: { step: Step; trackId: string }) {
  // Defensive: only keep slides whose src is a non-empty whitelisted string, so a
  // malformed object can't crash the viewer. useMemo keeps the array identity
  // stable per step.slides so the effects below don't re-bind every render.
  const slides = useMemo(
    () =>
      (step.slides ?? []).filter(
        (s) => !!s && typeof s.src === 'string' && IMG_SRC_RE.test(s.src),
      ),
    [step.slides],
  );
  const hasPdf = typeof step.pdfUrl === 'string' && /^https?:\/\//i.test(step.pdfUrl);
  // Our-bucket uploads → access-checked same-origin proxy; external PDFs → direct.
  const useProxy = hasPdf && isOurStoragePdf(step.pdfUrl!);
  const pdfRenderUrl = useProxy
    ? `/api/track/${encodeURIComponent(trackId)}/step/${encodeURIComponent(step.id)}/pdf`
    : step.pdfUrl;

  const [current, setCurrent] = useState(0);
  const [presenting, setPresenting] = useState(false);
  // The element promoted to fullscreen (mirrors PdfDeck's deckRef pattern).
  const carouselRef = useRef<HTMLDivElement>(null);
  // Element fullscreen is unavailable on some platforms (notably iPhone Safari);
  // hide the Present control there rather than offering a dead button.
  const fullscreenSupported =
    typeof document !== 'undefined' && !!document.fullscreenEnabled;

  // Reset to the first slide when the slide SET changes — keyed off the stable
  // step identity so switching between two SAME-LENGTH slides steps resets too.
  useEffect(() => {
    setCurrent(0);
  }, [step.id]);

  // Track THIS carousel's fullscreen state — another element going fullscreen
  // elsewhere (e.g. the PDF deck below) must not flip our "presenting" state.
  // Esc exits native fullscreen; this listener keeps the UI in sync.
  useEffect(() => {
    const onFsChange = () =>
      setPresenting(document.fullscreenElement === carouselRef.current);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const togglePresent = useCallback(() => {
    const el = carouselRef.current;
    if (!el || typeof el.requestFullscreen !== 'function') return;
    // Key off THIS element's fullscreen state (not "any element fullscreen") so the
    // button never exits some other component's fullscreen instead of presenting.
    if (document.fullscreenElement === el) {
      void document.exitFullscreen();
    } else {
      // If the browser denies fullscreen, keep the UI in sync (don't show "Exit").
      // On success, move focus into the carousel so keyboard + screen readers land
      // inside the presented content.
      el.requestFullscreen().then(() => el.focus()).catch(() => setPresenting(false));
    }
  }, []);

  // Keyboard: fullscreen mode. The carousel fills the screen, so listen globally —
  // but ONLY while presenting (never steals keys from the rest of the app).
  useEffect(() => {
    if (!presenting) return;
    const handler = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault();
        setCurrent((c) => Math.min(slides.length - 1, c + 1));
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        setCurrent((c) => Math.max(0, c - 1));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [presenting, slides.length]);

  // Keyboard navigation — SCOPED to the focused carousel (no window listener),
  // so arrow keys are never stolen from the rest of the app or the PDF deck
  // below. Defined during render → always reads the current `slides` length.
  // While presenting, the global listener above owns the keys (avoids a
  // double-advance when focus sits inside the fullscreened element).
  const onCarouselKey = (e: React.KeyboardEvent) => {
    if (presenting) return;
    if (e.repeat) return;
    if (slides.length === 0) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setCurrent((c) => Math.max(0, c - 1));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      setCurrent((c) => Math.min(slides.length - 1, c + 1));
    }
  };

  if (slides.length === 0 && !hasPdf) {
    return (
      <div className="mt-4 rounded-xl border border-dashed border-rule p-6 text-center text-sm text-ink-400 dark:border-clay-rule">
        Slides coming soon — content will appear here. You can still mark this paintra done.
      </div>
    );
  }

  const safeIdx = slides.length > 0 ? Math.min(current, slides.length - 1) : 0;
  const slide = slides[safeIdx];

  return (
    <div className="mt-4 flex flex-col gap-3">
      {slides.length > 0 && (
        <div
          ref={carouselRef}
          tabIndex={0}
          onKeyDown={onCarouselKey}
          className={
            presenting
              ? 'fixed inset-0 z-50 flex flex-col bg-[#1a1108] outline-none'
              : 'overflow-hidden rounded-xl border border-rule outline-none focus-visible:ring-2 focus-visible:ring-terracotta-300 dark:border-clay-rule'
          }
        >
          {/* Image */}
          <div
            className={
              presenting
                ? 'flex min-h-0 flex-1 items-center justify-center'
                : 'flex items-center justify-center bg-paper-100 dark:bg-clay-raised'
            }
          >
            <img
              src={slide.src}
              alt={slide.alt ?? ''}
              loading="lazy"
              decoding="async"
              referrerPolicy="no-referrer"
              className={
                presenting
                  ? 'max-h-full max-w-full object-contain'
                  : 'max-h-[420px] w-full object-contain'
              }
            />
          </div>

          {/* Caption */}
          {slide.caption && (
            <p
              className={
                presenting
                  ? 'shrink-0 border-t border-white/10 px-4 py-2 text-center text-sm text-paper-300'
                  : 'border-t border-rule px-4 py-2 text-xs text-ink-400 dark:border-clay-rule dark:text-paper-400'
              }
            >
              {slide.caption}
            </p>
          )}

          {/* Prev / dots / next — only when there are multiple slides */}
          {slides.length > 1 && (
            <div
              className={`flex shrink-0 items-center justify-between px-4 py-2 ${
                presenting ? 'border-t border-white/10' : 'border-t border-rule dark:border-clay-rule'
              }`}
            >
              <button
                type="button"
                onClick={() => setCurrent((c) => Math.max(0, c - 1))}
                disabled={safeIdx === 0}
                aria-label="Previous slide"
                className={`flex items-center gap-1 rounded-lg px-2 py-1 text-xs transition disabled:opacity-30 ${
                  presenting
                    ? 'text-paper-300 hover:bg-white/10 hover:text-paper-100'
                    : 'text-ink-500 hover:bg-paper-100 dark:hover:bg-clay-raised'
                }`}
              >
                <ChevronLeft className="h-4 w-4" /> Prev
              </button>

              <div className="flex items-center gap-1.5">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setCurrent(i)}
                    aria-label={`Slide ${i + 1}`}
                    aria-current={i === safeIdx ? 'true' : undefined}
                    className={`h-2 w-2 rounded-full transition ${
                      i === safeIdx
                        ? 'bg-terracotta-500'
                        : presenting
                          ? 'bg-white/25 hover:bg-white/50'
                          : 'bg-paper-300 hover:bg-ink-300 dark:bg-clay-rule dark:hover:bg-paper-400'
                    }`}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={() => setCurrent((c) => Math.min(slides.length - 1, c + 1))}
                disabled={safeIdx === slides.length - 1}
                aria-label="Next slide"
                className={`flex items-center gap-1 rounded-lg px-2 py-1 text-xs transition disabled:opacity-30 ${
                  presenting
                    ? 'text-paper-300 hover:bg-white/10 hover:text-paper-100'
                    : 'text-ink-500 hover:bg-paper-100 dark:hover:bg-clay-raised'
                }`}
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Present toggle + current / total */}
          <div
            className={`flex shrink-0 items-center justify-between px-4 py-1.5 text-xs ${
              presenting
                ? 'border-t border-white/10 text-paper-300'
                : 'border-t border-rule text-ink-400 dark:border-clay-rule'
            }`}
          >
            {fullscreenSupported ? (
              <button
                type="button"
                onClick={togglePresent}
                aria-label={presenting ? 'Exit fullscreen' : 'Present fullscreen'}
                title={presenting ? 'Exit fullscreen (Esc)' : 'Present fullscreen'}
                className={`flex items-center gap-1.5 rounded-lg px-2 py-1 transition ${
                  presenting
                    ? 'text-paper-300 hover:bg-white/10 hover:text-paper-100'
                    : 'text-ink-500 hover:bg-paper-100 dark:hover:bg-clay-raised'
                }`}
              >
                {presenting ? (
                  <Minimize2 className="h-3.5 w-3.5" />
                ) : (
                  <Maximize2 className="h-3.5 w-3.5" />
                )}
                {presenting ? 'Exit' : 'Present'}
              </button>
            ) : (
              <span />
            )}
            {/* aria-live announces slide advances to screen readers (incl. fullscreen). */}
            <span aria-live="polite">
              {safeIdx + 1} / {slides.length}
            </span>
          </div>
        </div>
      )}

      {hasPdf && (
        <>
          <Suspense
            fallback={
              <div className="flex min-h-[180px] items-center justify-center rounded-xl border border-rule bg-[#1a1108] text-sm text-paper-300 dark:border-clay-rule">
                Loading deck…
              </div>
            }
          >
            <PdfDeck
              url={pdfRenderUrl!}
              title={step.title}
              authed={useProxy}
              fallbackUrl={step.pdfUrl}
            />
          </Suspense>
          {/* Secondary fallback for CORS / download scenarios */}
          <a
            href={step.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 self-start text-xs text-ink-400 transition hover:text-terracotta-600 dark:text-paper-400 dark:hover:text-terracotta-300"
          >
            <ExternalLink className="h-3 w-3" />
            Open in new tab ↗
          </a>
        </>
      )}
    </div>
  );
}

function FurtherReading({ references }: { references?: { label: string; url: string }[] }) {
  const safe = (references ?? []).filter((r) => /^https?:\/\//i.test(r.url));
  if (safe.length === 0) return null;
  return (
    <div className="mt-5">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-400">
        <BookMarked className="h-3.5 w-3.5" />
        Further reading
      </p>
      <ul className="mt-2 flex flex-col gap-1.5">
        {safe.map((r, i) => (
          <li key={i}>
            <a
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-terracotta-600 hover:text-terracotta-700 dark:text-terracotta-300 dark:hover:text-terracotta-200"
            >
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              {r.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function prettyError(code: string): string {
  switch (code) {
    case 'rate_limited':
      return 'Slow down a moment — try again in a couple of seconds.';
    case 'track_unpublished':
      return "This kushti isn't published yet.";
    case 'step_not_found':
      return 'That paintra no longer exists.';
    case 'no_token':
      return 'Your session expired — please sign in again.';
    case 'exam_locked':
      return 'Finish the earlier steps first, then come back to this exam.';
    default:
      return 'Something went wrong saving that. Please try again.';
  }
}
